import { BANNER_V3_MOBILE_IMAGE_SIZES } from '@/components/banner-card/types';
import CardContent, {
    CardProgressPosition,
} from '@/components/card/card-content';
import CardImage from '@/components/card/card-image';
import { BlurredBackgroundImage } from '@/components/ui/blurred-background-image';
import { CardContainer } from '@rhg/components/card';
import { Box } from '@rhg/libs/material';
import {
    MaskOrientation,
    styleMaskedContainer,
    sxAppend,
} from '@rhg/libs/material/sx';
import { ForwardedRef, forwardRef, useMemo, useState } from 'react';
import { BannerCardV3Props } from '../types';

const styleCardContainer = {
    width: '100%',
    height: '100%',
};

const styleContentLayout = {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
};

const styleImageItem = { flexShrink: 0 };
const styleContentItem = { flex: 1, minHeight: 0 };

/**
 * If there is progress, reduce top padding and add negative margin to account for the added vertical space taken by the progress bar
 */
function styleContent(hasProgress?: boolean) {
    return { pt: hasProgress ? 0 : 4, mt: hasProgress ? -1 : 0 };
}

export const BannerCardV3Mobile = forwardRef<HTMLDivElement, BannerCardV3Props>(
    function BannerCardV3Mobile(
        {
            title,
            subtitle,
            content,
            category,
            progress,
            button,
            image,
            container,
            sx,
            'data-syntheticid': dataSyntheticId,
        }: BannerCardV3Props,
        ref: ForwardedRef<HTMLDivElement>
    ) {
        const [hasFocus, setHasFocus] = useState(false);

        const containerHandlers = useMemo(
            () => ({
                onMouseEnter: () => setHasFocus(true),
                onMouseLeave: () => setHasFocus(false),
                onFocus: () => setHasFocus(true),
                onBlur: () => setHasFocus(false),
                onClick: container?.onClick,
            }),
            [container?.onClick]
        );

        const imageButtonProps = {
            component: container?.component,
            href: container?.href,
            'data-cy': container?.['data-cy'],
        };

        return (
            <CardContainer
                ref={ref}
                isNotClickable={true}
                entireCardButtonProps={undefined}
                isEntireCardClickable={false}
                sx={sxAppend(styleCardContainer, sx)}
                trackButton="NotMapped"
                data-cy="bannerCard"
                data-syntheticid={dataSyntheticId}
            >
                <Box
                    sx={{
                        position: 'relative',
                        overflow: 'hidden',
                        height: '100%',
                    }}
                >
                    <BlurredBackgroundImage
                        src={image?.src}
                        orientation={MaskOrientation.Bottom}
                        sx={{ height: '100%' }}
                    >
                        <Box sx={styleContentLayout}>
                            <Box sx={styleImageItem}>
                                <Box
                                    sx={styleMaskedContainer(
                                        MaskOrientation.Top
                                    )}
                                >
                                    <CardImage
                                        src={image?.src}
                                        alt={image?.alt ?? ''}
                                        sx={
                                            image?.forcedSize
                                                ? { width: image?.forcedSize }
                                                : undefined
                                        }
                                        decorations={image?.decorations}
                                        iconFocus={image?.focusIcon}
                                        width={
                                            typeof image?.forcedSize ===
                                            'number'
                                                ? image?.forcedSize
                                                : // Full-bleed image: fall back to
                                                  // a viewport-relative `sizes` so
                                                  // next/image stops fetching 100vw.
                                                  image?.width ??
                                                  BANNER_V3_MOBILE_IMAGE_SIZES
                                        }
                                        isFocused={hasFocus}
                                        imageSideRadiusEnabled={
                                            image?.sideRadiusEnabled
                                        }
                                        showIconsOnFocused={
                                            image?.showIconsOnFocused
                                        }
                                        ratio="3:2"
                                        {...containerHandlers}
                                        aria-label={
                                            container?.onClick ||
                                            container?.href
                                                ? container?.['aria-label']
                                                : undefined
                                        }
                                        trackButton={container?.trackButton}
                                        trackProps={container?.trackProps}
                                        role={container?.role}
                                        {...imageButtonProps}
                                        {...image?.props}
                                    />
                                </Box>
                            </Box>
                            <Box sx={styleContentItem}>
                                <CardContent
                                    title={title.text}
                                    titleComponent={title.component}
                                    titleMaxLines={title.maxLines}
                                    titleTopSpacing={title.topSpacing}
                                    titleBottomSpacing={title.bottomSpacing}
                                    subtitle={subtitle?.text}
                                    subtitleVariant={subtitle?.variant}
                                    subtitleMaxLines={subtitle?.maxLines}
                                    icon={title.icon}
                                    tag={content?.tag}
                                    action={content?.action}
                                    categoryTitle={category?.title}
                                    categoryTitleColor={category?.titleColor}
                                    categoryDescription={category?.description}
                                    categoryIcon={category?.icon}
                                    progress={progress?.value}
                                    progressText={progress?.text}
                                    progressTextAlt={progress?.textAlt}
                                    progressPosition={
                                        CardProgressPosition.Bottom
                                    }
                                    buttonGroup={button?.group}
                                    showButtonGroupOnBottom
                                    additionalAction={button?.additionalAction}
                                    variant="carousel"
                                    sx={sxAppend(
                                        styleContent(
                                            typeof progress?.value === 'number'
                                        ),
                                        content?.sx
                                    )}
                                />
                            </Box>
                        </Box>
                    </BlurredBackgroundImage>
                </Box>
            </CardContainer>
        );
    }
);
